import modals from '@/components/modals'
import { createContext, useContext, useState, ReactNode, Suspense } from 'react'
import { toast } from '@vnetwork/datacentral-ui/feedback'

// ModalOptions: Sử dụng union type cho `value` để tự động hiểu các giá trị modal có thể mở
interface ModalOptions {
    open: boolean
    value: string | null
    props: any
}

interface ModalContextProps {
    setModal: <T extends object>(
        value: string,
        props?: Omit<T, 'open' | 'onClose'>
    ) => void
    closeModal: () => void
    open: boolean
}

// Tạo ModalContext với generic để nhận loại `value` từ `ModalOption`
const ModalContext = createContext<ModalContextProps | undefined>(undefined)

export const useModal = (): ModalContextProps => {
    const context = useContext(ModalContext) as ModalContextProps
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider')
    }
    return context
}

interface ModalProviderProps {
    children: ReactNode
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
    const [modal, setModalState] = useState<ModalOptions>({
        open: false,
        value: null,
        props: null,
    })

    const setModal = (value: string, props?: any) => {
        setModalState({ open: true, value, props })
    }

    const closeModal = () => {
        setModalState({ open: false, value: null, props: null })
    }

    // Lấy component modal đang mở
    const ActiveModalComponent = modals?.[modal?.value as string]

    if (!ActiveModalComponent && modal?.value) {
        toast.error('Modal component not found')
        console.error('Modal component not found')
    }

    return (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- legacy @ts-ignore, baselined (check:lint-disables); use @ts-expect-error with a reason
        // @ts-ignore
        <ModalContext.Provider
            value={{ open: modal?.open, setModal, closeModal }}
        >
            {children}
            {modal.open && ActiveModalComponent && (
                // Modals load on first open; nothing shows until the chunk arrives.
                <Suspense fallback={null}>
                    <ActiveModalComponent
                        onClose={closeModal}
                        open
                        {...modal.props}
                    />
                </Suspense>
            )}
        </ModalContext.Provider>
    )
}
